import { doc, p } from '@atlaskit/adf-utils/builders';

import type { BulkIssuesListType, IssueObject } from '../providers/BulkCreateContextProvider';
import type { CreateBulkIssuesResponse } from '../__types__/apiUtils';

export const getClosestTableFromSelection = (activeSelection: Selection | undefined | null) => {
	if (activeSelection && activeSelection.rangeCount > 0) {
		// Get the first range from the selection
		const range = activeSelection.getRangeAt(0);
		// Start from the common ancestor container of the range
		let node: Node | null = range.commonAncestorContainer;
		// If the node is a text node, move up to its parent element
		if (node?.nodeType === Node.TEXT_NODE) {
			node = node.parentElement;
		}
		// Traverse up the DOM tree to find the closest <table> element
		while (node && node.nodeType === Node.ELEMENT_NODE) {
			if ((node as HTMLElement).tagName === 'TABLE') {
				return node as HTMLTableElement;
			}
			node = node.parentElement;
		}
	}
	return null;
};

/*
 * Using the any as the type of the mapping for now, as we don't know the exact shape of the mapping object with custom fields yet.
 * I am planning to do something like this, but it is subject to change:
 * mapping = {'summary': 0, 'description': 1, ...} where the key is the field name and the value is the column index.
 */
export const mapTableToIssues = (
	mapping: { [key: string]: number },
	table: HTMLTableElement,
	setTableBulkIssuesList: (issues: BulkIssuesListType) => void,
) => {
	let issueIndex = 0;
	const rows = table.rows;
	const issues: IssueObject[] = [];

	// Determine the starting row index based on whether the first row is a header
	// Note: the issue insertion API does not count the header as the first row
	const startRowIndex = rows.length > 0 && rows[0].cells[0].tagName.toLowerCase() === 'th' ? 1 : 0;

	for (let i = startRowIndex; i < rows.length; i++) {
		// Using any for now, as we don't know the exact shape of the issue object with the given mapping.
		const issue: { [key: string]: any } = {
			issueType: {
				iconUrl: '',
				name: '',
				id: '',
			},
			rowIndex: i - startRowIndex,
		};
		let emptySummary = true;
		let emptyDescription = true;
		for (const key in mapping) {
			const textContent = rows[i]?.cells[mapping[key]]?.textContent?.trim() || '';
			if (key === 'description') {
				issue[key] = doc(p(textContent));
				emptyDescription = !textContent;
			} else {
				issue[key] = textContent || 'Untitled';
				emptySummary = key === 'summary' && !textContent;
			}
		}
		if (!emptySummary || !emptyDescription) {
			issue.id = String(issueIndex++);
			issues.push(issue as IssueObject);
		}
	}
	setTableBulkIssuesList(issues);
};

export const updateIssuesWithRowIndex = (
	result: CreateBulkIssuesResponse,
	rowIndices: number[],
) => {
	const failedElementNumbers = result.errors.map((error) => error.failedElementNumber);
	const updatedRowIndices = rowIndices.filter(
		(_, elementNumber) => !failedElementNumbers.includes(elementNumber),
	);
	return result.issues.map((issue, index) => {
		return {
			...issue,
			rowIndex: updatedRowIndices[index],
		};
	});
};
